interface StreamingConfig {
  /** リクエスト先のURL */
  url: string;
  /** HTTPメソッド */
  method: 'GET' | 'POST';
  /** リクエストボディ（POSTリクエストの場合） */
  body?: BodyInit;
  /** 追加のHTTPヘッダー */
  additionalHeaders?: Record<string, string>;
  /** 受け入れるレスポンスの種類のリスト */
  acceptedTypes: readonly string[];
  /** AbortControllerのシグナル */
  signal?: AbortSignal;
}

// cause は既存の呼び出し側が文字列として読むため、形を変えない。
export class StreamingResponseError extends Error {
  readonly status: number;
  readonly data: unknown;

  constructor(status: number, data?: unknown) {
    super('リクエストに失敗しました', {
      cause: (data as { error?: unknown } | null | undefined)?.error,
    });
    this.status = status;
    this.data = data;
  }
}

/**
 * Server-Sent Events (SSE) ストリームを処理する非同期ジェネレーター関数
 *
 * @param config - ストリーミングリクエストの設定
 *
 */
export async function* handleStreamingResponse<T>(
  config: StreamingConfig,
): AsyncGenerator<T> {
  const {
    url,
    method,
    body,
    additionalHeaders = {},
    acceptedTypes,
    signal,
  } = config;

  // ストリーミングリクエストの初期化
  const response = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...additionalHeaders,
    },
    body: body || undefined,
    signal,
  });

  if (response.status !== 200 || !response.body) {
    if (response.body) {
      let jsonData: unknown = { error: undefined };
      // 本文が複数のチャンクに分かれて届くことがあるため、最初のチャンクではなく本文全体を読んでから解析する。
      try {
        jsonData = await response.json();
      } catch {
        // この下にあるthrowでエラーを投げる
      }
      throw new StreamingResponseError(response.status, jsonData);
    }
    throw new StreamingResponseError(response.status);
  }
  let chunk = '';
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  while (true) {
    const { done, value } = await reader.read();

    if (done) break;
    if (!value) continue;

    chunk += decoder.decode(value, { stream: true });
    if (!chunk.includes('\n')) continue; // データの最後に改行がない場合はまだデータが完全ではないので次のデータを待つ

    const lines = chunk.split('\n');
    chunk = lines.pop() ?? ''; // 最後の行がまだ完全でない可能性があるため、それを次のチャンクと結合

    for (let line of lines) {
      if (!line.startsWith('data: ')) continue;
      line = line.slice(6); // `data: `部分を取り除く

      try {
        const json = JSON.parse(line.trim());

        if (json.type && acceptedTypes.includes(json.type)) {
          yield json;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('ストリーミングデータの JSONパースに失敗しました', error);
        break;
      }
    }
  }
  reader.releaseLock();
}
